import React, { useId } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { TypedMemo } from '../../../common/types';
import { SongsBrewMark } from '../../../music24/artistHome/songsBrew/SongsBrewMark';
import { songsBrewMessages as messages } from '../../../music24/artistHome/songsBrew/songsBrewMessages';
import { SongsBrewSendTrack } from '../../../music24/artistHome/songsBrew/SongsBrewSendTrack';
import type { SongsBrewStatus } from '../../../music24/artistHome/songsBrew/useSongsBrewState';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../settingsCards.module.scss';
import notifications from './notifications.module.scss';
import styles from './SongsBrewSwitchRow.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface Props {
  status: SongsBrewStatus;
  sendTrackUrl: string;
  needsIssueLink: boolean;
  onChange: (on: boolean) => void;
}

/**
 * The SongsBrew newsletter row with its switch and the line under it (design: songsbrew-promotion
 * c2-c7): "Send us your track" while on, the refused line in the error colour, or the issue link
 * line after a subscribe that the platform could not do.
 */
const SongsBrewSwitchRowComponent: React.FC<Props> = ({ status, sendTrackUrl, needsIssueLink, onChange }) => {
  const intl = useIntl();
  const id = useId();
  const isOn = status === 'subscribed';
  const isRefused = status === 'refused';
  const hasLine = isOn || isRefused || needsIssueLink;

  return (
    <>
      <div className={`${cards.row}${hasLine ? ` ${cards.rowFlat}` : ''}`}>
        <SongsBrewMark size="sm" />
        <label className={cards.rowText} htmlFor={id}>
          <span className={cards.rowTitle}>{intl.formatMessage(messages.settingsTitle)}</span>
          <span className={cards.rowSub}>{intl.formatMessage(messages.settingsDescription)}</span>
        </label>
        <div className={cards.rowEnd}>
          <div className={`form-check form-switch ${notifications.switch}`}>
            <input
              id={id}
              type="checkbox"
              role="switch"
              className="form-check-input"
              checked={isOn}
              disabled={isRefused}
              onChange={(event) => onChange(event.target.checked)}
            />
          </div>
        </div>
      </div>
      {isOn && <SongsBrewSendTrack sendTrackUrl={sendTrackUrl} className={styles.line} />}
      {isRefused && (
        <p className={`${styles.line} ${styles.negative}`} role="alert">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span>{intl.formatMessage(messages.refused)}</span>
        </p>
      )}
      {!isOn && !isRefused && needsIssueLink && (
        <p className={styles.line} role="status">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span>{intl.formatMessage(messages.unsubscribedTitle)}</span>
        </p>
      )}
    </>
  );
};

export const SongsBrewSwitchRow = TypedMemo(SongsBrewSwitchRowComponent);
